import {
  BlockEditor,
  type BlockEditorCustomBlock,
  type BlockEditorImages,
  IconBanknote
} from '@sequencehq/core-components'
import {
  QUOTE_CONTAINER_WIDTH,
  type QuoteVariableType
} from '@sequencehq/quote-content'
import { PricingWidget } from 'Cube/view/common/widgets/Pricing/PhasesWidget'
import { getVariableLabel } from '../utils/quoteVariables'
import { resolveFileUrl } from '../utils/resolveFileUrl'
import { ACCEPTED_IMAGE_MIME_TYPES, uploadFile } from '../utils/uploadFile'
import { PRICING_CUSTOM_BLOCK } from './adapters/presentationToTiptap'
import { useQuoteBlockEditor } from './useQuoteBlockEditor'
import { useQuoteVariableValues } from './useQuoteVariableValues'

const PLACEHOLDER = "Write, '/' for commands, '{' for variables..."

const QUOTE_VARIABLES = (
  [
    'CUSTOMER_NAME',
    'CUSTOMER_ADDRESS',
    'CUSTOMER_COUNTRY',
    'RECIPIENTS',
    'QUOTE_OWNER',
    'START_DATE',
    'END_DATE',
    'DURATION',
    'PAYMENT_TERMS',
    'CURRENCY'
  ] satisfies QuoteVariableType[]
).map(type => ({ type, label: getVariableLabel(type) }))

/**
 * The pricing stays editable for users who can't edit the rest of the content,
 * and its expanded phases live above the editor, so they survive the block
 * being moved.
 */
const CUSTOM_BLOCKS: BlockEditorCustomBlock[] = [
  {
    type: PRICING_CUSTOM_BLOCK,
    title: 'Pricing',
    icon: IconBanknote,
    isSingleton: true,
    isInteractiveWhenReadOnly: true,
    render: () => <PricingWidget data-testid="quote.blockEditor.phaseBlock" />
  }
]

/** A new table's 3 columns share the quote's width equally. */
const NEW_TABLE_COLUMN_WIDTH = Number.parseInt(QUOTE_CONTAINER_WIDTH, 10) / 3

const IMAGES: BlockEditorImages = {
  upload: uploadFile,
  resolveUrl: resolveFileUrl,
  acceptedMimeTypes: ACCEPTED_IMAGE_MIME_TYPES
}

export const QuoteBlockEditor = () => {
  const { editorKey, initialContent, onChange, isEditable, dimmed } =
    useQuoteBlockEditor()
  const variableValues = useQuoteVariableValues()

  return (
    <BlockEditor
      key={editorKey}
      initialContent={initialContent}
      onChange={onChange}
      isEditable={isEditable}
      dimmed={dimmed}
      placeholder={PLACEHOLDER}
      variables={QUOTE_VARIABLES}
      variableValues={variableValues}
      customBlocks={CUSTOM_BLOCKS}
      images={IMAGES}
      newTableColumnWidth={NEW_TABLE_COLUMN_WIDTH}
      suggestionMenuTestId="quote.blockEditor.slashMenu"
      data-testid="quote.blockEditor"
    />
  )
}
